import { RoleName } from "@/api/users-types";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { EpisodeBreadcrumbs } from "@/components/ui/breadcrumbs/EpisodeBreadcrumbs";
import { Link } from "@/components/ui/Link";
import { BoxLoader } from "@/components/ui/loaders";
import { TextBoxRaw } from "@/components/ui/TextBox";
import { useHasRole } from "@/hooks/use-has-role";
import { useEpisodeById } from "@/queries/episodes/use-episode-by-id";
import { useStoryGenerationMetadata } from "@/queries/stories/use-story-generation-metadata";
import { Chip, Stack, Typography } from "@mui/material";
import { useParams } from "react-router";

export function GenerationMetadata() {
  const { id } = useParams();

  const { data, error, isLoading } = useStoryGenerationMetadata(id);
  const {
    data: episode,
    error: episodeError,
    isLoading: episodeIsLoading,
  } = useEpisodeById(id);

  const prompt = episode?.prompt;
  const promptTitle = `${prompt?.logicalPrompt.name}, revision ${prompt?.revision}`;
  const imagePrompt = episode?.imagePrompt;
  const imagePromptTitle = imagePrompt
    ? `${imagePrompt.logicalPrompt.name}, revision ${imagePrompt.revision}`
    : null;

  const canVisitPromptAndConfig = useHasRole([
    RoleName.episodeManager,
    RoleName.promptEngineer,
  ]);

  return (
    <Stack gap={2} mb={2}>
      <BoxLoader isLoading={isLoading || episodeIsLoading} />
      <ApiErrorAlert error={error} />
      <ApiErrorAlert error={episodeError} />
      <Stack
        sx={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: "33px", // height of Button, set in case no buttons are displayed to have consistent height across different cases
        }}
      >
        {episode && (
          <EpisodeBreadcrumbs
            id={episode.id}
            category={episode.category}
            gender={episode.gender}
            story={episode.story}
            subcategory={episode.subcategory}
          />
        )}
      </Stack>

      <Header>
        {episode?.pages.find((p) => p.pageNumber === 0)?.text ||
          "Missing story title!"}
      </Header>
      <Text>{`Page size: ${episode?.pageSize || "Missing page size!"}`}</Text>
      <Text>
        {`Number of pages: ${episode?.numberOfPages || "Missing number of pages!"}`}
      </Text>
      <Text>{`Model name: ${episode?.modelName || "Missing model name!"}`}</Text>

      <SecondaryHeader>Prompts</SecondaryHeader>
      <Stack direction={"row"} gap={1} alignItems="center">
        <Text>
          {`Text prompt: `}
          {prompt && canVisitPromptAndConfig ? (
            <Link
              to={`/prompts/${prompt?.logicalPrompt.id}/revisions/${prompt?.revision}`}
            >
              {promptTitle}
            </Link>
          ) : prompt ? (
            promptTitle
          ) : (
            "No text prompt!"
          )}
        </Text>
        {prompt?.logicalPrompt.deletedAt && (
          <Chip
            label={"Prompt deleted"}
            size="small"
            color="error"
            variant="outlined"
          />
        )}
      </Stack>
      <Stack direction={"row"} gap={1} alignItems="center">
        <Text>
          {`Image prompt: `}
          {imagePromptTitle && canVisitPromptAndConfig ? (
            <Link
              to={`/prompts/${imagePrompt?.logicalPrompt.id}/revisions/${imagePrompt?.revision}`}
            >
              {imagePromptTitle}
            </Link>
          ) : imagePrompt ? (
            imagePromptTitle
          ) : (
            "No image prompt!"
          )}
        </Text>
        {imagePrompt?.logicalPrompt.deletedAt && (
          <Chip
            label={"Prompt deleted"}
            size="small"
            color="error"
            variant="outlined"
          />
        )}
      </Stack>

      <SecondaryHeader>Config</SecondaryHeader>
      <Text>
        {`Story config: `}
        {episode?.config && canVisitPromptAndConfig ? (
          <Link
            to={`/story-configs/${episode.config.storyConfig?.id}/revisions/${episode.config.revision}`}
          >
            {`${episode.config.storyConfig?.storyType}, revision ${episode.config.revision}`}
          </Link>
        ) : episode?.config ? (
          `${episode.config.storyConfig?.storyType}, revision ${episode.config.revision}`
        ) : (
          "No story config!"
        )}
      </Text>

      <SecondaryHeader>Params</SecondaryHeader>
      <TextBoxRaw>
        {data?.params
          ? JSON.stringify(data.params, undefined, 2)
          : "Missing params!"}
      </TextBoxRaw>

      <SecondaryHeader>Raw Text</SecondaryHeader>
      <TextBoxRaw>{data?.rawStoryText || "Missing text!"}</TextBoxRaw>

      <SecondaryHeader>Error</SecondaryHeader>
      <TextBoxRaw>{data?.error || "No error recorded."}</TextBoxRaw>
    </Stack>
  );
}

function Header({ children }: { children: React.ReactNode }) {
  return (
    <Typography component={"h1"} variant="h5">
      {children}
    </Typography>
  );
}

function SecondaryHeader({ children }: { children: React.ReactNode }) {
  return (
    <Typography component={"h2"} variant="h6">
      {children}
    </Typography>
  );
}

function Text({ children }: { children: React.ReactNode }) {
  return (
    <Typography component={"p"} color={"textSecondary"}>
      {children}
    </Typography>
  );
}
